import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import PropertyForm from '@/components/PropertyForm';
import { Property, PropertyFormData } from '@/types';
import { PropertyService } from '@/services/propertyService';
import { useAuthStore } from '@/store/authStore';

const PropertyEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('Property ID is required');
      setLoading(false);
      return;
    }

    const loadProperty = async () => {
      try {
        setLoading(true);
        setError(null);
        const propertyData = await PropertyService.getProperty(id);
        
        if (!propertyData) {
          setError('Property not found');
          return;
        }

        // Check permission (client-side check, also enforced on server)
        if (user?.role !== 'admin' && user?.id !== propertyData.agentId) {
          setError('You do not have permission to edit this property');
          return;
        }

        setProperty(propertyData);
      } catch (err: any) {
        setError(err.message || 'Failed to load property');
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [id, user]);

  const handleUpdateProperty = async (data: PropertyFormData, imageFiles?: File[], documentFiles?: File[]) => {
    if (!property || !id) return;

    setSaving(true);
    try {
      let updatedMetadata = { ...data.metadata };

      // Handle new image uploads
      if (imageFiles && imageFiles.length > 0) {
        const existingCount = updatedMetadata.images?.length || 0;
        const uploadPromises = imageFiles.map((file, index) => 
          PropertyService.uploadPropertyImage(id, file, existingCount + index)
        );
        
        const newImageUrls = await Promise.all(uploadPromises);
        updatedMetadata.images = [...(updatedMetadata.images || []), ...newImageUrls];
      }

      // Handle new document uploads
       if (documentFiles && documentFiles.length > 0) {
         const uploadPromises = documentFiles.map((file, index) => 
           PropertyService.uploadPropertyDocument(id, file, `doc-${Date.now()}-${index}`)
         );
         const newDocUrls = await Promise.all(uploadPromises);
         updatedMetadata.documents = [...(updatedMetadata.documents || []), ...newDocUrls];
      }

      const finalData = {
          ...data,
          metadata: updatedMetadata
      };

      await PropertyService.updateProperty(property.id, finalData);
      navigate(`/properties/${property.id}`);
    } catch (err: any) {
      console.error('Update error:', err);
      setError(err.message || 'Failed to update property');
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to leave? Unsaved changes will be lost.')) {
       navigate(`/properties/${id}`);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!property) return;
    
    setDeleting(true);
    try {
      await PropertyService.deleteProperty(property.id);
      navigate('/properties');
    } catch (err: any) {
      setError(err.message || 'Failed to delete property');
      setDeleteDialogOpen(false);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/properties')}>
          Back to Properties
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link color="inherit" href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
            Dashboard
          </Link>
          <Link color="inherit" href="/properties" onClick={(e) => { e.preventDefault(); navigate('/properties'); }}>
            Properties
          </Link>
          <Link color="inherit" href={`/properties/${id}`} onClick={(e) => { e.preventDefault(); navigate(`/properties/${id}`); }}>
            {property?.metadata.title || 'Property'}
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>
        
        <Button 
          variant="outlined" 
          color="error" 
          onClick={handleDeleteClick}
        >
          Delete Property
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Edit Property: {property?.metadata.title}
        </Typography>
        
        <PropertyForm
          property={property || undefined}
          onSubmit={handleUpdateProperty}
          onCancel={handleCancel}
          loading={saving}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Property?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{property?.metadata.title}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyEditPage;
